import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { toast } from 'sonner'
import type { Currency } from '@/lib/db-types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function todayString(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** "2025-01-05" → "1/5" */
export function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return `${d.getMonth() + 1}/${d.getDate()}`
}

/** "2025-01-05" → "2025/1/5" */
export function formatFullDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
}

/** 現在の月を "YYYY-MM" 形式で返す */
export function getCurrentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

/** "2025-01" → "2025年1月" */
export function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number)
  return `${year}年${month}月`
}

/** "2025-01" → 31 (その月の日数) */
export function getDaysInMonth(yearMonth: string): number {
  const [year, month] = yearMonth.split('-').map(Number)
  return new Date(year, month, 0).getDate()
}

/** 月を delta 分シフトした "YYYY-MM" を返す */
export function shiftMonth(yearMonth: string, delta: number): string {
  const [year, month] = yearMonth.split('-').map(Number)
  const d = new Date(year, month - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const ERROR_MESSAGE_MAP: Record<string, string> = {
  'Personal categories cannot be used in household transactions':
    '個人カテゴリは家計の取引には使用できません。',
}

export function mutationErrorHandler(fallback: string) {
  return (error: unknown) => {
    const msg = error instanceof Error ? error.message : ''
    toast.error(ERROR_MESSAGE_MAP[msg] || msg || fallback)
  }
}

export type ScopeFilter = 'all' | 'household' | 'personal'

export type ExpenseTypeFilter = 'all' | 'fixed' | 'variable'

export type PaidByFilter = 'all' | (string & {})

export function filterByScope<T extends { scope: string }>(
  transactions: T[],
  scopeFilter: ScopeFilter,
): T[] {
  if (scopeFilter === 'all') return transactions
  return transactions.filter(tx => tx.scope === scopeFilter)
}

export function filterByExpenseType<T extends { input_method: string }>(
  transactions: T[],
  expenseType: ExpenseTypeFilter,
): T[] {
  if (expenseType === 'all') return transactions
  if (expenseType === 'fixed') return transactions.filter(tx => tx.input_method === 'recurring')
  return transactions.filter(tx => tx.input_method !== 'recurring')
}

export function filterByPaidBy<T extends { paid_by: string }>(
  transactions: T[],
  paidByFilter: PaidByFilter,
): T[] {
  if (paidByFilter === 'all') return transactions
  return transactions.filter(tx => tx.paid_by === paidByFilter)
}

/** 指定日の月曜日を返す (ISO week: 月曜始まり) */
export function getMonday(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const day = d.getDay()
  // 日曜(0)→6, 月曜(1)→0, 火曜(2)→1, ...
  const diff = day === 0 ? 6 : day - 1
  d.setDate(d.getDate() - diff)
  return d
}

/** Date → "YYYY-MM-DD" */
export function formatDateStr(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** 取引の表示名: 店舗名 → 品名1件目(複数なら「など」付き) → '(不明)' の優先順で返す */
export function getTransactionDisplayName(tx: { store_name: string | null; items: { name: string }[] }): string {
  if (tx.store_name) return tx.store_name
  if (tx.items.length === 0) return '(不明)'
  if (tx.items.length === 1) return tx.items[0].name
  return `${tx.items[0].name}など`
}

/**
 * 金額を ¥1,234 / $12.99 形式にフォーマット。compact: true で10万以上を
 * ¥12.3万 に省略 (JPY only)。
 *
 * JPY 通常表示は常に整数 (円単位) に丸める — 平均など計算で出る小数は
 * 「4.5円」のように現実の金額として意味がないため。compact 表記は 万 単位
 * での 0.1 刻みなので意味があり、そのまま残す。
 */
export function formatAmount(amount: number, currency: Currency = 'JPY', compact = false): string {
  if (currency === 'USD') {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  if (compact && amount >= 100000) {
    const v = amount / 10000
    return `¥${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1).replace(/0$/, '')}万`
  }
  return `¥${Math.round(amount).toLocaleString()}`
}

/** JPY なら小数部を除去した文字列を返す (入力・トグル切替用) */
export function sanitizeAmountStr(value: string, currency: Currency): string {
  if (currency === 'JPY' && value.includes('.')) {
    return value.split('.')[0]
  }
  return value
}

/** JPY なら整数に丸めた数値を返す (保存用) */
export function sanitizeAmountNum(value: number, currency: Currency): number {
  return currency === 'JPY' ? Math.round(value) : value
}

/** "YYYY-MM" → その月にかかる全週 (月曜〜日曜) の配列 */
export function getWeeksInMonth(yearMonth: string): { start: Date; end: Date }[] {
  const [year, month] = yearMonth.split('-').map(Number)
  const firstDay = new Date(year, month - 1, 1)
  const lastDay = new Date(year, month, 0) // 月末日

  const firstMonday = getMonday(firstDay)
  const weeks: { start: Date; end: Date }[] = []

  let current = new Date(firstMonday)
  while (current <= lastDay) {
    const end = new Date(current)
    end.setDate(end.getDate() + 6)
    weeks.push({ start: new Date(current), end })
    current.setDate(current.getDate() + 7)
  }

  return weeks
}
